import { isManagerOrAbove } from '@/domain/permissions';
import type { Feedback, FeedbackKind, FeedbackStatus } from '@/domain/types';
import { rt } from './runtime';
import { DomainError, inTx, newId, newMeta, nowIso, putAudited, touch } from './write';

export const FEEDBACK_KIND_LABEL: Record<FeedbackKind, string> = { problem: 'Something’s wrong', idea: 'An idea', confusing: 'Something’s confusing' };
export const FEEDBACK_STATUS_LABEL: Record<FeedbackStatus, string> = { new: 'New', seen: 'Looked at', done: 'Done' };

/** "iPad · Safari", "Android · Chrome", "Windows · Edge": enough to spot device-only problems. */
export function deviceDescription(ua: string = typeof navigator === 'undefined' ? '' : navigator.userAgent): string {
  const device = /iPad/.test(ua) || (/Macintosh/.test(ua) && /Mobile|Touch/.test(ua)) ? 'iPad' : /iPhone/.test(ua) ? 'iPhone' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS X|Macintosh/.test(ua) ? 'Mac' : 'Other';
  const browser = /Edg\//.test(ua) ? 'Edge' : /CriOS|Chrome\//.test(ua) ? 'Chrome' : /FxiOS|Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : 'a browser';
  const app = typeof window !== 'undefined' && window.matchMedia?.('(display-mode: standalone)').matches ? ' (home-screen app)' : '';
  return `${device} · ${browser}${app}`;
}

/** Anyone can send feedback, from any screen. */
export async function sendFeedback(input: { kind: FeedbackKind; text: string; page: string }): Promise<Feedback> {
  const text = input.text.trim();
  if (text.length < 3) throw new DomainError('Type what happened or what you’d like.');
  if (text.length > 2000) throw new DomainError('That’s a bit long. Keep it under 2,000 characters.');
  return inTx(['feedback', 'people'], async () => {
    const me = await rt().db.people.get(rt().actor.userId);
    const f: Feedback = { ...newMeta(newId('fbk-')), kind: input.kind, text, page: input.page.slice(0, 200), by: rt().actor.userId, byName: me?.name ?? rt().actor.name, at: nowIso(), device: deviceDescription(), status: 'new', reply: '' };
    await putAudited('feedback', f, null, { action: 'feedback.send', summary: `Sent feedback: ${FEEDBACK_KIND_LABEL[f.kind].toLowerCase()}` });
    return f;
  });
}

/** A manager marks feedback looked at or done, with an optional note on what was done. */
export async function updateFeedback(id: string, patch: { status?: FeedbackStatus; reply?: string }): Promise<void> {
  if (!isManagerOrAbove(rt().actor.role)) throw new DomainError('Only a manager can change feedback.');
  await inTx(['feedback'], async () => {
    const before = await rt().db.feedback.get(id);
    if (!before) throw new DomainError('That feedback no longer exists.');
    const next = touch(before, { ...(patch.status ? { status: patch.status } : {}), ...(patch.reply != null ? { reply: patch.reply.trim().slice(0, 1000) } : {}) });
    await putAudited('feedback', next, before, { action: 'feedback.update', summary: `Marked feedback “${FEEDBACK_STATUS_LABEL[next.status].toLowerCase()}”` });
  });
}
